<script setup>
// ========================================
// Imports
// ========================================
import { computed } from 'vue'
import { useDisplay } from 'vuetify'
import { useI18n } from 'vue-i18n'

// ========================================
// Props & Models
// ========================================
const props = defineProps({
  length: {
    type: Number,
    required: true,
  },
  pageSizeItems: {
    type: Array,
    default: () => [5, 10, 15, 30],
  },
  label: {
    type: String,
    default: null,
  },
  tooltip: {
    type: String,
    default: null,
  },
  showResultsSummary: {
    type: Boolean,
    default: false,
  },
  filteredCount: {
    type: Number,
    default: 0,
  },
  totalCount: {
    type: Number,
    default: 0,
  },
  what: {
    type: String,
    default: '',
  },
  totalChipped: {
    type: Boolean,
    default: false,
  },
})

const page = defineModel({
  type: Number,
  required: true,
})

const perPage = defineModel('perPage', {
  type: Number,
  required: true,
})

// ========================================
// Logic & Computed
// ========================================
const { t } = useI18n()
const { xs, mobile } = useDisplay()

const selectLabel = computed(() => props.label ?? t('Per page'))
const tooltipText = computed(() => props.tooltip ?? t('number_of_results_per_page'))

const rangeStart = computed(() => {
  if (props.filteredCount === 0) return 0

  return (page.value - 1) * perPage.value + 1
})

const rangeEnd = computed(() => Math.min(page.value * perPage.value, props.filteredCount))
const isFiltered = computed(() => props.filteredCount !== props.totalCount)
const rangeText = computed(() => `${rangeStart.value} - ${rangeEnd.value}`)
const whatSuffix = computed(() => props.what ? ` ${props.what}` : '')
</script>

<template>
  <div>
    <VCol
      cols="12"
      class="d-flex justify-center align-center flex-column flex-sm-row"
    >
      <VPagination
        v-model="page"
        :length="props.length"
        :total-visible="xs ? 1 : 5"
        density="comfortable"
        color="primary"
      />
      <span class="mt-3 mt-sm-0 ml-sm-4">
        <VSelect
          v-model="perPage"
          style="inline-size: 110px;"
          :label="selectLabel"
          :items="props.pageSizeItems"
          density="compact"
          color="secondary"
          hide-details
          class="ml-4"
        />
        <VTooltip
          v-if="!mobile"
          activator="parent"
        >
          {{ tooltipText }}
        </VTooltip>
      </span>
    </VCol>

    <VCol
      v-if="showResultsSummary"
      cols="12"
      class="d-flex justify-center align-center flex-column flex-sm-row"
    >
      <div
        v-if="filteredCount > 0"
        class="text-body-2 text-medium-emphasis text-center"
      >
        <template v-if="isFiltered">
          {{ rangeText }} z {{ filteredCount }} {{ $t('filtered') }}{{ whatSuffix }} ({{ $t('of') }}
          <VChip
            v-if="totalChipped"
            color="primary"
            size="small"
            class="mx-1"
          >
            {{ totalCount }}
          </VChip>
          <template v-else>
            {{ totalCount }}
          </template>
          {{ $t('all') }})
        </template>
        <template v-else>
          {{ rangeText }} {{ $t('of') }}
          <VChip
            v-if="totalChipped"
            color="primary"
            size="small"
            class="mx-1"
          >
            {{ totalCount }}
          </VChip>
          <template v-else>
            {{ totalCount }}
          </template>
          {{ whatSuffix }}
        </template>
      </div>
    </VCol>
  </div>
</template>


